import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import supabase from '../lib/supabase';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit3, FiTrash2, FiRefreshCw, FiCheck, FiX, FiExternalLink, FiAlertCircle } = FiIcons;

const AdminEventSources = () => {
  const [sources, setSources] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [testingSource, setTestingSource] = useState(null);
  const [testResults, setTestResults] = useState(null);

  const [formData, setFormData] = useState({
    org_id: '',
    source_type: 'rss',
    url: '',
    parser_key: '',
    active: true
  });

  useEffect(() => {
    fetchSources();
    fetchOrgs();
  }, []);

  const fetchSources = async () => {
    try {
      const { data, error } = await supabase
        .from('event_sources')
        .select(`
          *,
          orgs (
            name,
            type,
            state
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSources(data || []);
    } catch (error) {
      console.error('Error fetching sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgs = async () => {
    try {
      const { data, error } = await supabase
        .from('orgs')
        .select('id, name, type, state')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setOrgs(data || []);
    } catch (error) {
      console.error('Error fetching orgs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingSource) {
        const { error } = await supabase
          .from('event_sources')
          .update(formData)
          .eq('id', editingSource.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('event_sources')
          .insert([formData]);

        if (error) throw error;
      }

      resetForm();
      fetchSources();
    } catch (error) {
      console.error('Error saving source:', error);
      alert('Error saving source: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      org_id: '',
      source_type: 'rss',
      url: '',
      parser_key: '',
      active: true
    });
    setShowAddForm(false);
    setEditingSource(null);
  };

  const handleEdit = (source) => {
    setFormData({
      org_id: source.org_id || '',
      source_type: source.source_type,
      url: source.url,
      parser_key: source.parser_key || '',
      active: source.active
    });
    setEditingSource(source);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this source?')) return;

    try {
      const { error } = await supabase
        .from('event_sources')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchSources();
    } catch (error) {
      console.error('Error deleting source:', error);
      alert('Error deleting source: ' + error.message);
    }
  };

  const testSource = async (source) => {
    setTestingSource(source.id);
    setTestResults(null);

    try {
      let endpoint = '';
      switch (source.source_type) {
        case 'rss':
          endpoint = 'parse-rss';
          break;
        case 'ics':
          endpoint = 'parse-ics';
          break;
        case 'html':
          endpoint = 'parse-html';
          break;
        default:
          throw new Error('Unsupported source type');
      }

      const params = new URLSearchParams({
        url: source.url
      });

      if (source.parser_key) {
        params.append('parser', source.parser_key);
      }

      const response = await fetch(`/.netlify/functions/${endpoint}?${params}`);
      const data = await response.json();

      setTestResults({
        success: data.success,
        items: data.items || [],
        error: data.error
      });

    } catch (error) {
      console.error('Error testing source:', error);
      setTestResults({
        success: false,
        error: error.message,
        items: []
      });
    } finally {
      setTestingSource(null);
    }
  };

  const triggerFetch = async (sourceId) => {
    try {
      const response = await fetch('/.netlify/functions/events-fetch');
      const data = await response.json();
      
      if (data.success) {
        alert('Events fetch triggered successfully!');
        fetchSources(); // Refresh to see updated last_fetch_at
      } else {
        alert('Error triggering fetch: ' + data.error);
      }
    } catch (error) {
      console.error('Error triggering fetch:', error);
      alert('Error triggering fetch: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-patriot-navy">Event Sources</h1>
            <p className="text-gray-600 mt-2">Manage RSS, ICS, and HTML sources for event aggregation</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => triggerFetch()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center"
            >
              <SafeIcon icon={FiRefreshCw} className="h-4 w-4 mr-2" />
              Fetch All
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-patriot-red text-white px-4 py-2 rounded-lg hover:bg-patriot-red-dark transition-colors duration-200 flex items-center"
            >
              <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
              Add Source
            </button>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md p-6 mb-8"
          >
            <h2 className="text-xl font-semibold text-patriot-navy mb-4">
              {editingSource ? 'Edit Source' : 'Add New Source'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization
                  </label>
                  <select
                    value={formData.org_id}
                    onChange={(e) => setFormData({ ...formData, org_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                    required
                  >
                    <option value="">Select Organization</option>
                    {orgs.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name} ({org.type}, {org.state})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source Type
                  </label>
                  <select
                    value={formData.source_type}
                    onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                  >
                    <option value="rss">RSS Feed</option>
                    <option value="ics">ICS Calendar</option>
                    <option value="html">HTML Scraping</option>
                    <option value="api">API</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                  placeholder="https://example.com/events.rss"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parser Key (optional)
                </label>
                <input
                  type="text"
                  value={formData.parser_key}
                  onChange={(e) => setFormData({ ...formData, parser_key: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                  placeholder="wordpress-events, eventbrite-embed, etc."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="h-4 w-4 text-patriot-red focus:ring-patriot-red border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                  Active (will be included in automatic fetches)
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200"
                >
                  {editingSource ? 'Update' : 'Add'} Source
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Sources List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Fetch
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sources.map((source) => (
                  <tr key={source.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {source.orgs?.name || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {source.orgs?.type}, {source.orgs?.state}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {source.source_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          {source.url}
                          <SafeIcon icon={FiExternalLink} className="h-3 w-3 ml-1" />
                        </a>
                      </div>
                      {source.parser_key && (
                        <div className="text-xs text-gray-500">
                          Parser: {source.parser_key}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {source.active ? (
                          <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500 mr-1" />
                        ) : (
                          <SafeIcon icon={FiX} className="h-4 w-4 text-red-500 mr-1" />
                        )}
                        <span className={`text-sm ${source.active ? 'text-green-700' : 'text-red-700'}`}>
                          {source.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {source.last_status && (
                        <div className="text-xs text-gray-500 mt-1">
                          {source.last_status}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {source.last_fetch_at
                        ? new Date(source.last_fetch_at).toLocaleString()
                        : 'Never'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => testSource(source)}
                          disabled={testingSource === source.id}
                          className="text-blue-600 hover:text-blue-900 disabled:text-gray-400"
                          title="Test Parse"
                        >
                          <SafeIcon 
                            icon={testingSource === source.id ? FiRefreshCw : FiExternalLink} 
                            className={`h-4 w-4 ${testingSource === source.id ? 'animate-spin' : ''}`} 
                          />
                        </button>
                        <button
                          onClick={() => handleEdit(source)}
                          className="text-patriot-red hover:text-patriot-red-dark"
                          title="Edit"
                        >
                          <SafeIcon icon={FiEdit3} className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(source.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Test Results */}
        {testResults && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 bg-white rounded-lg shadow-md p-6"
          >
            <h3 className="text-lg font-semibold text-patriot-navy mb-4">
              Test Results
              {testResults.success ? (
                <SafeIcon icon={FiCheck} className="inline h-5 w-5 text-green-500 ml-2" />
              ) : (
                <SafeIcon icon={FiAlertCircle} className="inline h-5 w-5 text-red-500 ml-2" />
              )}
            </h3>

            {!testResults.success && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-700">{testResults.error}</p>
              </div>
            )}

            {testResults.items && testResults.items.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Found {testResults.items.length} event(s)
                </p>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {testResults.items.slice(0, 5).map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900">{item.title}</h4>
                      {item.start_time && (
                        <p className="text-sm text-gray-600">
                          {new Date(item.start_time).toLocaleString()}
                        </p>
                      )}
                      {item.venue_name && (
                        <p className="text-sm text-gray-600">{item.venue_name}</p>
                      )}
                      {item.description && (
                        <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                          {item.description}
                        </p>
                      )}
                    </div>
                  ))}
                  {testResults.items.length > 5 && (
                    <p className="text-sm text-gray-500 text-center">
                      ... and {testResults.items.length - 5} more events
                    </p>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <SafeIcon icon={FiRefreshCw} className="h-8 w-8 text-patriot-red animate-spin" />
            <span className="ml-2 text-gray-600">Loading sources...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEventSources;